import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { profileApi } from '../services/api';

const TierContext = createContext(null);

export const TierProvider = ({ children }) => {
  const { member, isAuthenticated } = useAuth();
  const [tierConfig, setTierConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quotesUsedThisMonth, setQuotesUsedThisMonth] = useState(0);
  const [enquiriesUsedThisMonth, setEnquiriesUsedThisMonth] = useState(0);
  const [teamMemberCount, setTeamMemberCount] = useState(0);

  const fetchTierConfig = useCallback(async () => {
    if (!member?.member_id) {
      setLoading(false);
      return;
    }

    try {
      const response = await profileApi.getTierConfig(member.member_id);
      const data = response.data;
      setTierConfig(data);
      setQuotesUsedThisMonth(data.quotes_used_this_month ?? 0);
      setEnquiriesUsedThisMonth(data.enquiries_used_this_month ?? 0);
      setTeamMemberCount(data.team_member_count ?? 0);
    } catch (error) {
      console.error('Error fetching tier config:', error);
      // Set default config based on tier
      let tier = member?.tier?.toLowerCase() || 'registered_user';
      // Normalize tier name: "registered user" -> "registered_user"
      tier = tier.replace(/\s+/g, '_');
      
      // Default feature access for Registered User tier
      const defaultFeatureAccess = {
        dashboard: true,
        directory: true,
        quotes: true,
        events: true,
        messaging: false,
        rewards: false,
        analytics: false,
        ma_portal: false,
        referrals: true,
        team: false,
        complaints: true
      };
      
      // Override for premium tiers
      if (tier === 'gold' || tier === 'platinum' || tier === 'rhodium' || tier === 'titanium' || tier === 'founder') {
        defaultFeatureAccess.messaging = true;
        defaultFeatureAccess.rewards = true;
        defaultFeatureAccess.team = true;
      }
      if (tier === 'platinum' || tier === 'rhodium' || tier === 'titanium' || tier === 'founder') {
        defaultFeatureAccess.analytics = true;
      }
      if (tier === 'rhodium' || tier === 'titanium' || tier === 'founder') {
        defaultFeatureAccess.ma_portal = true;
      }
      
      setTierConfig({
        tier: member?.tier,
        tier_name: tier,
        display_name: member?.tier || 'Registered User',
        points_system_enabled: false,
        feature_access: defaultFeatureAccess,
        enabled_features: {}, // all enabled when empty
        max_quotes_per_month: tier === 'registered_user' ? 10 : tier === 'silver' ? 5 : tier === 'gold' ? 25 : 0,
        max_enquiries_per_month: tier === 'registered_user' ? 10 : tier === 'silver' ? 5 : tier === 'gold' ? 25 : 0,
        max_team_members: tier === 'registered_user' ? 3 : tier === 'silver' ? 2 : tier === 'gold' ? 5 : 0,
        restrictions: []
      });
    } finally {
      setLoading(false);
    }
  }, [member?.member_id, member?.tier]);

  useEffect(() => {
    if (isAuthenticated && member?.member_id) {
      fetchTierConfig();
    } else {
      setTierConfig(null);
      setLoading(false);
    }
  }, [isAuthenticated, member?.member_id, fetchTierConfig]);

  // Check if a feature is globally enabled (not disabled by admin)
  const isFeatureEnabled = (featureKey) => {
    if (!tierConfig?.enabled_features) return true;
    return tierConfig.enabled_features[featureKey] !== false;
  };

  // Check if a feature is accessible (globally enabled AND allowed for this tier)
  const hasFeatureAccess = (featureKey) => {
    if (!isFeatureEnabled(featureKey)) return false;
    if (!tierConfig?.feature_access) return true;
    return tierConfig.feature_access[featureKey] !== false;
  };

  // Restriction messages per type (from spec)
  const RESTRICTION_MESSAGES = {
    full: 'Your account access has been restricted. Please contact admin.',
    quotes: 'Contact admin',
    events: 'Contact admin',
    chat: 'Contact admin',
    ma: 'Contact admin',
    directory: 'Your visibility is restricted to other members, Pls Contact admin.',
  };

  // Check if member has a specific restriction type
  const hasRestriction = (restrictionType) => {
    const restrictions = tierConfig?.restrictions || [];
    return restrictions.includes(restrictionType);
  };

  // Get display message for a restriction type
  const getRestrictionMessage = (restrictionType) => {
    return RESTRICTION_MESSAGES[restrictionType] || 'Contact admin';
  };

  // Check if user can create more quotes this month
  const canCreateQuote = () => {
    if (!tierConfig) return true;
    const limit = tierConfig.max_quotes_per_month;
    if (limit === 0) return true; // Unlimited
    return quotesUsedThisMonth < limit;
  };

  // Check if user can create more enquiries this month
  const canCreateEnquiry = () => {
    if (!tierConfig) return true;
    const limit = tierConfig.max_enquiries_per_month;
    if (limit === 0) return true; // Unlimited
    return enquiriesUsedThisMonth < limit;
  };

  // Get remaining quota
  const getRemainingQuotes = () => {
    if (!tierConfig) return null;
    const limit = tierConfig.max_quotes_per_month;
    if (limit === 0) return 'Unlimited';
    return Math.max(0, limit - quotesUsedThisMonth);
  };

  const getRemainingEnquiries = () => {
    if (!tierConfig) return null;
    const limit = tierConfig.max_enquiries_per_month;
    if (limit === 0) return 'Unlimited';
    return Math.max(0, limit - enquiriesUsedThisMonth);
  };

  // Team member limit (admin + invited)
  const canInviteTeamMember = () => {
    if (!tierConfig) return true;
    const limit = tierConfig.max_team_members;
    if (limit === 0) return true; // Unlimited
    return teamMemberCount < limit;
  };

  const getRemainingTeamSlots = () => {
    if (!tierConfig) return null;
    const limit = tierConfig.max_team_members;
    if (limit === 0) return 'Unlimited';
    return Math.max(0, limit - teamMemberCount);
  };

  // Map feature keys to route paths
  const featureToRouteMap = {
    'dashboard': '/dashboard',
    'directory': '/directory',
    'quotes': '/quotes',
    'events': '/event-connect',
    'messaging': '/chat',
    'rewards': '/rewards',
    'analytics': '/analytics',
    'ma_portal': '/mergers-acquisitions',
    'referrals': '/referrals',
    'team': '/team',
    'complaints': '/complaints'
  };

  const routeToFeatureMap = Object.entries(featureToRouteMap).reduce((acc, [key, value]) => {
    acc[value] = key;
    return acc;
  }, {});

  // Check if a route is accessible
  const isRouteAccessible = (path) => {
    const featureKey = routeToFeatureMap[path];
    if (!featureKey) return true; // Unknown routes are accessible
    return hasFeatureAccess(featureKey);
  };

  const value = {
    tierConfig,
    loading,
    pointsSystemEnabled: tierConfig?.points_system_enabled === true,
    isFeatureEnabled,
    hasFeatureAccess,
    hasRestriction,
    getRestrictionMessage,
    canCreateQuote,
    canCreateEnquiry,
    canInviteTeamMember,
    getRemainingQuotes,
    getRemainingEnquiries,
    getRemainingTeamSlots,
    isRouteAccessible,
    featureToRouteMap,
    routeToFeatureMap,
    quotesUsedThisMonth,
    enquiriesUsedThisMonth,
    teamMemberCount,
    setQuotesUsedThisMonth,
    setEnquiriesUsedThisMonth,
    refreshTierConfig: fetchTierConfig
  };

  return (
    <TierContext.Provider value={value}>
      {children}
    </TierContext.Provider>
  );
};

export const useTier = () => {
  const context = useContext(TierContext);
  if (!context) {
    throw new Error('useTier must be used within a TierProvider');
  }
  return context;
};

export default TierContext;
